import { useState } from 'react';
import { Trash2, X, AlertTriangle } from 'lucide-react';

interface ForceDeleteTestDialogProps {
  testName: string;
  executionCount: number;
  onConfirm: (password: string) => void;
  onCancel: () => void;
  isProcessing: boolean;
}

export const ForceDeleteTestDialog = ({ 
  testName, 
  executionCount, 
  onConfirm, 
  onCancel, 
  isProcessing 
}: ForceDeleteTestDialogProps) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      onConfirm(password);
    }
  };

  return (
    <div data-component="ForceDeleteTestDialog" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Force Delete Test</h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4">
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 mb-2">
                <strong>Warning:</strong> This test has {executionCount} execution{executionCount !== 1 ? 's' : ''}.
              </p>
              <p className="text-sm text-red-800">
                Force deleting "<strong>{testName}</strong>" will permanently remove the test configuration. 
                The executions will remain in the system but will be orphaned. This cannot be undone.
              </p>
            </div>

            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Enter password to confirm:
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isProcessing}
              placeholder="Password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100"
              autoFocus
            />
          </div>

          <div className="flex gap-3 p-4 bg-gray-50 rounded-b-lg">
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !password.trim()}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Force Delete Test
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
